/**
 * Synthetic beat injector. Treated as a first-class test input per the plan.
 *
 * Two ways to fire:
 *   - Manual: pulse() once.
 *   - Auto: setIntervalMs(N) for steady ticks; null disables.
 *
 * Inject by calling `consume(now)` once per frame; if a tick is due you
 * receive { tick: true, confidence: 1 } which the spike OR-merges into the
 * audio frame's beat field.
 */

export type BeatInjector = {
  consume: (now: number) => { tick: boolean; confidence: number };
  pulse: () => void;
  setIntervalMs: (ms: number | null) => void;
  getIntervalMs: () => number | null;
};

export function createBeatInjector(): BeatInjector {
  let pendingPulse = false;
  let intervalMs: number | null = null;
  let nextTickTime = 0;

  return {
    consume(now: number) {
      let tick = false;
      if (pendingPulse) {
        tick = true;
        pendingPulse = false;
      }
      if (intervalMs !== null) {
        if (nextTickTime === 0) nextTickTime = now + intervalMs / 1000;
        if (now >= nextTickTime) {
          tick = true;
          nextTickTime += intervalMs / 1000;
          if (now > nextTickTime + intervalMs / 500) nextTickTime = now + intervalMs / 1000;
        }
      } else {
        nextTickTime = 0;
      }
      return { tick, confidence: tick ? 1 : 0 };
    },
    pulse() {
      pendingPulse = true;
    },
    setIntervalMs(ms) {
      intervalMs = ms;
      nextTickTime = 0;
    },
    getIntervalMs() {
      return intervalMs;
    },
  };
}

const AUTO_BEAT_CYCLE: Array<number | null> = [null, 500, 750, 1000];

export function nextAutoBeat(current: number | null): number | null {
  const idx = AUTO_BEAT_CYCLE.findIndex((v) => v === current);
  const next = (idx + 1) % AUTO_BEAT_CYCLE.length;
  return AUTO_BEAT_CYCLE[next] ?? null;
}
